import { faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { CGForeignSharesDetailsForm } from ".";
import { IForeignSharesForm, IFormITR1, IReviewCapitalGains } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const CGForeignSharesDetails: React.FC = () => {
	const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<IForeignSharesForm | null>(null);
	const [activeItem, setActiveItem] = useState<IForeignSharesForm | null>(null);

	// used in both edit and delete cases
	const [activeItemIdx, setActiveItemIdx] = useState<string>("");

	const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
	const ForeignSharesDestails = cloneDeep(values.IncomeDetails?.ForeignShares || []);

	const handleDeleteInterestItem = async () => {
		ForeignSharesDestails.splice(Number(activeItemIdx), 1);
		setFieldValue("IncomeDetails.ForeignShares", ForeignSharesDestails);
		setShowDeleteModal(() => null);
		setActiveItem(() => null);
		setActiveItemIdx(() => "");
		handleTotalCalculation();
	};

	const handleTotalCalculation = () => {
		const ForeignSharesGains =
			ForeignSharesDestails?.reduce((acc, curr) => {
				const value = parseInt(curr.Gains);
				return acc + (isNaN(value) ? 0 : value);
			}, 0) || 0;

		const CapitalGains: IReviewCapitalGains = {
			...values.CapitalGains,
			TotalForeignSharesGain: ForeignSharesGains
		};

		const TotalCapitalGains = Object.values(CapitalGains)?.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

		const DATA = {
			CapitalGains: CapitalGains,
			TotalCapitalGains: TotalCapitalGains
		};

		setValues({ ...cloneDeep(values), ...DATA });
		setFieldValue("IncomeDetails.ForeignShares", ForeignSharesDestails);
		setFieldValue("LastSavedTimeStamp", new Date().getTime());
	};

	return (
		<>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>Foreign Shares</h5>
					<PreventEditComponent>
						<Button
							variant="secondary"
							size="sm"
							onClick={() => {
								setShowAddFormCanvas(() => true);
							}}
						>
							<FASvgIcon icon={faPlus} />
							Add
						</Button>
					</PreventEditComponent>
				</div>

				<dl className="eds-list eds-dl eds-inline-between">
					{ForeignSharesDestails.map((foreignShare, index) => {
						return (
							<div key={`any-asset-${index}`} className="eds-list--item">
								<dt>
									{foreignShare.DescriptionAssetSold} ({foreignShare.TypeAssets})
								</dt>
								<dd>
									<span>
										<PriceDisplay price={foreignShare?.Gains} />
									</span>
									<Button
										variant="link"
										onClick={() => {
											setShowAddFormCanvas(() => true);
											setActiveItem(() => foreignShare);
											setActiveItemIdx(() => String(index));
										}}
									>
										View
										<PreventEditComponent>
											<>/Edit</>
										</PreventEditComponent>
									</Button>
									<PreventEditComponent>
										<Button
											variant="transparent"
											className="icon-only"
											onClick={() => {
												setShowDeleteModal(() => foreignShare);
												setActiveItemIdx(() => String(index));
											}}
										>
											<FASvgIcon icon={faTrash} />
										</Button>
									</PreventEditComponent>
								</dd>
							</div>
						);
					})}
				</dl>
			</section>

			<CGForeignSharesDetailsForm
				showCanvas={showAddFormCanvas}
				handleCloseBtnPress={(foreignSharesData) => {
					if (foreignSharesData) {
						if (activeItem) {
							ForeignSharesDestails.splice(Number(activeItemIdx), 1, foreignSharesData);
						} else {
							ForeignSharesDestails.push(foreignSharesData);
						}
						handleTotalCalculation();
					}
					setShowAddFormCanvas(() => false);
					setActiveItem(() => null);
					setActiveItemIdx(() => "");
				}}
				formValues={activeItem}
			/>

			<ConfirmationModal
				showModal={!!showDeleteModal}
				iconColorClass="text-danger"
				title="Are you sure?"
				message={<p>You want to delete this</p>}
				onYesBtnClick={() => handleDeleteInterestItem()}
				onNoBtnClick={() => setShowDeleteModal(() => null)}
				icon={faTrashAlt}
			/>
		</>
	);
};

export default CGForeignSharesDetails;
